import { Suspense, lazy } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';
import ErrorGuard from '../guards/ErrorGuard';
import RoleBasedGuard from '../guards/RolebasedGuard';
import { adminRoles, LocusProducts, Permissions, Roles, Routes } from '../constants';
import { ReportCreationContext } from '../contexts/ReportCreationContext';

const Loadable = (Component: any) => (props: any) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

const LandingScreen = Loadable(lazy(() => import('../components/LandingScreen')));
const DashboardLayout = Loadable(lazy(() => import('../components/DashboardLayout')));
const Dashboard = Loadable(lazy(() => import('../components/Dashboard')));
const CreateUserForm = Loadable(lazy(() => import('../pages/Create-User')));

const Home = Loadable(lazy(() => import('../pages/Home')));
const StudyArea = Loadable(lazy(() => import('../pages/StudyArea')));
const Projects = Loadable(lazy(() => import('../pages/Projects')));
const PassengerView = Loadable(lazy(() => import('../pages/PassengerView')));
const PerformView = Loadable(lazy(() => import('../pages/PerformView')));
const TransitView = Loadable(lazy(() => import('../pages/TransitView')));
const TruckView = Loadable(lazy(() => import('../pages/TruckView')));
const Collections = Loadable(lazy(() => import('../pages/Collections')));
const CollectionsViewPage = Loadable(lazy(() => import('../pages/CollectionsViewPage')));
const TimePeriod = Loadable(lazy(() => import('../pages/TimePeriod')));
const MapLayers = Loadable(lazy(() => import('../pages/MapLayers')));
const ZoneSystems = Loadable(lazy(() => import('../pages/ZoneSystem')));
const Equity = Loadable(lazy(() => import('../pages/Equity')));
const CreateMapLayer = Loadable(lazy(() => import('../pages/CreateNewMapLayer')));
const UpdateMapLayer = Loadable(lazy(() => import('../pages/UpdateMapLayer')));
const CreateNewEquity = Loadable(lazy(() => import('../pages/CreateNewEquity')));
const CreateNewStudyArea = Loadable(lazy(() => import('../pages/CreateNewStudyArea')));
const CreateNewZoneSystem = Loadable(lazy(() => import('../pages/CreateNewZoneSystem')));
const UserGuides = Loadable(lazy(() => import('../pages/UserGuides')));
const UseCases = Loadable(lazy(() => import('../pages/UseCases')));
const FAQs = Loadable(lazy(() => import('../pages/FAQs')));
const ContactSupport = Loadable(lazy(() => import('../pages/ContactSupport')));
const Theme = Loadable(lazy(() => import('../pages/Theme')));
const Features = Loadable(lazy(() => import('../pages/Features')));
const ProjectCreation = Loadable(lazy(() => import('../pages/ProjectCreation')));
const Clients = Loadable(lazy(() => import('../pages/Clients')));
const ClientCreation = Loadable(lazy(() => import('../pages/ClientCreation')));
const Users = Loadable(lazy(() => import('../pages/Users')));
const Organization = Loadable(lazy(() => import('../pages/Organization')));
const Profile = Loadable(lazy(() => import('../pages/Profile')));
const TruckDashboard = Loadable(lazy(() => import('../pages/TruckDashboard')));
const TransitDashboard = Loadable(lazy(() => import('../pages/TransitDashboard')));
const StatsDashboard = Loadable(lazy(() => import('../pages/StatsDashboard')));
const WhatsNew = Loadable(lazy(() => import('../pages/WhatsNew')));
const WhatsNewCreation = Loadable(lazy(() => import('../components/WhatsNew/WhatsNewCreation')));
const ProjectDetailsView = Loadable(lazy(() => import('../components/Projects/ProjectDetailsView')));
const ClientUpdation = Loadable(lazy(() => import('../components/Clients/ClientUpdation')));

const Guide = Loadable(lazy(() => import('../pages/Guide')));
const PerformanceReportCreation = Loadable(lazy(() => import('../pages/CreateReport')));
const ReportDatasetSelect = Loadable(lazy(() => import('../components/PerformanceReport/ReportDatasetSelect')));
const ReportTypeSelect = Loadable(lazy(() => import('../components/PerformanceReport/ReportTypeSelect')));
const ReportDateRanges = Loadable(lazy(() => import('../components/PerformanceReport/ReportDateRanges')));
const ReportAdvancedDate = Loadable(lazy(() => import('../components/PerformanceReport/ReportAdvancedDate')));
const ReportTimePeriods = Loadable(lazy(() => import('../components/PerformanceReport/ReportTimePeriods')));
const ReportAdvancedTime = Loadable(lazy(() => import('../components/PerformanceReport/ReportAdvancedTime')));
const ReportRoutesMap = Loadable(lazy(() => import('../components/PerformanceReport/ReportRoutesMap')));
const ReportAdvancedRoute = Loadable(lazy(() => import('../components/PerformanceReport/ReportAdvancedRoute')));
const ReportDetails = Loadable(lazy(() => import('../components/PerformanceReport/ReportDetails')));
const ReportAreaMap = Loadable(lazy(() => import('../components/PerformanceReport/ReportAreaMap')));
const ReportAdvancedArea = Loadable(lazy(() => import('../components/PerformanceReport/ReportAdvancedArea')));

const SignUp = Loadable(lazy(() => import('../pages/SignUp')));
const RequestPage = Loadable(lazy(() => import('../pages/RequestPage')));
const ErrorPage = Loadable(lazy(() => import('../pages/ErrorPage')));
const DataRequestPage = Loadable(lazy(() => import('../components/RequestPage/MapDataRequest')));
const VideoWalkThrough = Loadable(lazy(() => import('../pages/VideoWalkThrough')));
const DataLayer = Loadable(lazy(() => import('../pages/DataLayer')));
const DataLayerRequest = Loadable(lazy(() => import('../components/RequestPage/DataLayerRequest')));
const Networks = Loadable(lazy(() => import('../pages/Networks')));
const NetworkCreation = Loadable(lazy(() => import('../pages/CreateNewNetwork')));
const SafeView = Loadable(lazy(() => import('../pages/SafeView')));
const DenverIframe = Loadable(lazy(() => import('../components/DenverIframe')));
const SouthCarolinaSelectAnalysis = Loadable(lazy(() => import('../components/SouthCarolinaIframe')));
const SelectLinkView = Loadable(lazy(() => import('../pages/SelectLinkView')));
const PerformanceReportView = Loadable(lazy(() => import('../pages/PerformanceReportView')));
const BikePedFlowsView = Loadable(lazy(() => import('../pages/BikePedFlowsView')));
const TruckSelectLinkIframe = Loadable(lazy(() => import('../pages/TruckSelectLinkIframe')));
const ParkRideView = Loadable(lazy(() => import('../pages/ParkRideView')));
const SelectLinkReport = Loadable(lazy(() => import('../pages/SelectLinkReport')));
const Superset = Loadable(lazy(() => import('../pages/Superset')));
const PerformanceReport = Loadable(lazy(() => import('../pages/PerformanceReport')));
const SelectLinkCreation = Loadable(lazy(() => import('../pages/SelectLinkCreation')));

const routes: any[] = [
  {
    path: Routes.truck,
    element: (
      <TruckDashboard />
    )
  },
  {
    path: Routes.transit,
    element: (
      <TransitDashboard />
    )
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <LandingScreen />
      </AuthGuard>
    )
  },
  {
    path: '/:slug/signup',
    element: (
      <AuthGuard>
        <SignUp />
      </AuthGuard>
    )
  },
  {
    path: '/denverpnr',
    element: (
      <AuthGuard>
        <DenverIframe />
      </AuthGuard>
    )
  },
  {
    path: '/sc-analysis',
    element: (
      <AuthGuard>
        <SouthCarolinaSelectAnalysis />
      </AuthGuard>
    )
  },
  {
    path: Routes.truckSelectLink,
    element: (
      <AuthGuard>
        <TruckSelectLinkIframe />
      </AuthGuard>
    )
  },
  {
    path: `${Routes.person}/:viewId`,
    element: (
      <GuestGuard>
        <PassengerView />
      </GuestGuard>
    ),
  },
  {
    path: `${Routes.perform}/:viewId`,
    element: (
      <GuestGuard>
        <PerformView />
      </GuestGuard>
    ),
  },
  {
    path: `${Routes.personPlus}/:viewId`,
    element: (
      <GuestGuard>
        <PassengerView />
      </GuestGuard>
    ),
  },
  {
    path: `${Routes.truck}/:viewId`,
    element: (
      <GuestGuard>
        <TruckView />
      </GuestGuard>
    ),
  },
  {
    path: `${Routes.transit}/:viewId`,
    element: (
      <GuestGuard>
        <TransitView />
      </GuestGuard>
    ),
  },
  {
    path: `${Routes.safe}/:viewId`,
    element: (
      <GuestGuard>
        <SafeView />
      </GuestGuard>
    )
  },
  {
    path: `${Routes.selectLink}/:viewId`,
    element: (
      <GuestGuard>
        <SelectLinkView />
      </GuestGuard>
    )
  },
  {
    path: `${Routes.performanceReportView}/:id`,
    element: (
      <GuestGuard>
        <PerformanceReportView />
      </GuestGuard>
    )
  },
  {
    path: `${Routes.bikePedFlows}/:viewId`,
    element: (
      <GuestGuard>
        <BikePedFlowsView />
      </GuestGuard>
    )
  },
  {
    path: `${Routes.parkRide}/:viewId`,
    element: (
      <GuestGuard>
        <ParkRideView />
      </GuestGuard>
    )
  },
  {
    path: '/error',
    element: (
      <GuestGuard>
        <ErrorPage />
      </GuestGuard>
    ),
  },
  {
    path: '/',
    element: (
      <GuestGuard>
        <DashboardLayout />
      </GuestGuard>
    ),
    children: [
      {
        path: Routes.dashboard,
        element: <Dashboard />
      },
      {
        path: Routes.clients,
        element: (
          <RoleBasedGuard
            RoutePermissions={[Permissions.ADD_CLIENT_ADMIN]}
            RoleAccess={adminRoles}
          >
            <Clients />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.clientCreation,
        element: (
          <RoleBasedGuard
            RoutePermissions={[Permissions.ADD_CLIENT_ADMIN]}
            RoleAccess={[Roles.SITE_ADMIN]}
          >
            <ClientCreation />
          </RoleBasedGuard>
        )
      },
      {
        path: `${Routes.clients}/:clientId`,
        element: (
          <RoleBasedGuard
            RoutePermissions={[Permissions.ADD_CLIENT_ADMIN]}
            RoleAccess={adminRoles}
          >
            <ClientUpdation />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.home,
        element: (
          <RoleBasedGuard>
            <Home />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.studyArea,
        element: (
          <RoleBasedGuard>
            <StudyArea />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.studyAreaCreation,
        element: (
          <RoleBasedGuard
            clientAccess
            RoleAccess={[Roles.SITE_ADMIN, Roles.CLIENT_ADMIN, Roles.USER]}
          >
            <CreateNewStudyArea />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.projects,
        element: (
          <RoleBasedGuard>
            <Projects />
          </RoleBasedGuard>
        )
      },
      {
        path: `${Routes.projects}/:id/view`,
        element: (
          <RoleBasedGuard>
            <Projects />
          </RoleBasedGuard>
        )
      },
      {
        path: `${Routes.projects}/:id`,
        element: (
          <RoleBasedGuard>
            <ProjectDetailsView />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.collections,
        element: (
          <RoleBasedGuard>
            <Collections />
          </RoleBasedGuard>
        )
      },
      {
        path: `${Routes.collections}/:id`,
        element: (
          <RoleBasedGuard>
            <CollectionsViewPage />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.timeofDay,
        element: (
          <RoleBasedGuard>
            <TimePeriod />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.mapLayers,
        element: (
          <RoleBasedGuard>
            <MapLayers />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.mapLayerCreation,
        element: (
          <RoleBasedGuard
            RoleAccess={[Roles.SITE_ADMIN, Roles.CLIENT_ADMIN, Roles.USER]}
          >
            <CreateMapLayer
              productName={LocusProducts.Truck}
            />
          </RoleBasedGuard>
        )
      },
      {
        path: `${Routes.mapLayers}/:id`,
        element: (
          <RoleBasedGuard
            RoleAccess={[Roles.SITE_ADMIN, Roles.CLIENT_ADMIN, Roles.USER]}
          >
            <UpdateMapLayer />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.zoneSystem,
        element: (
          <RoleBasedGuard>
            <ZoneSystems />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.zoneSystemCreation,
        element: (
          <RoleBasedGuard
            RoleAccess={[Roles.SITE_ADMIN, Roles.CLIENT_ADMIN, Roles.USER]}
          >
            <CreateNewZoneSystem />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.equityCreation,
        element: (
          <RoleBasedGuard
            RoleAccess={[Roles.SITE_ADMIN, Roles.CLIENT_ADMIN, Roles.USER]}
          >
            <CreateNewEquity />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.userGuides,
        element: (
          <RoleBasedGuard>
            <UserGuides />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.equity,
        element: (
          <RoleBasedGuard>
            <Equity />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.useCases,
        element: (
          <RoleBasedGuard>
            <UseCases />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.faqs,
        element: (
          <RoleBasedGuard>
            <FAQs />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.contactSupport,
        element: (
          <RoleBasedGuard>
            <ContactSupport />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.theme,
        element: (
          <RoleBasedGuard RoutePermissions={[Permissions.ADD_CLIENT_ADMIN]}>
            <Theme />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.features,
        element: (
          <RoleBasedGuard RoutePermissions={[Permissions.ADD_CLIENT_ADMIN]}>
            <Features />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.projectCreation,
        element: (
          <RoleBasedGuard
            clientAccess
            RoleAccess={[Roles.SITE_ADMIN, Roles.CLIENT_ADMIN, Roles.USER]}
          >
            <ProjectCreation />
          </RoleBasedGuard>
        )
      },
      {
        path: `${Routes.addDashboard}/:id`,
        element: (
          <RoleBasedGuard>
            <ProjectCreation />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.users,
        element: (
          <RoleBasedGuard RoutePermissions={[Permissions.ADD_USER]}>
            <Users />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.organization,
        element: (
          <RoleBasedGuard RoutePermissions={[Permissions.ADD_USER]}>
            <Organization />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.stats,
        element: (
          <RoleBasedGuard RoutePermissions={[Permissions.ADD_USER]}>
            <StatsDashboard />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.profile,
        element: (
          <RoleBasedGuard>
            <Profile />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.guide,
        element: (
          <RoleBasedGuard>
            <Guide />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.whatsNew,
        element: (
          <RoleBasedGuard>
            <WhatsNew />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.whatsNewRelease,
        element: (
          <RoleBasedGuard
            RoutePermissions={[Permissions.ADD_CLIENT_ADMIN]}
            RoleAccess={[Roles.SITE_ADMIN]}
          >
            <WhatsNewCreation />
          </RoleBasedGuard>
        )
      },
      {
        path: `${Routes.whatsNewRelease}/:id`,
        element: (
          <RoleBasedGuard
            RoutePermissions={[Permissions.ADD_CLIENT_ADMIN]}
            RoleAccess={[Roles.SITE_ADMIN]}
          >
            <WhatsNewCreation />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.dataRequest,
        element: (
          <RoleBasedGuard>
            <DataRequestPage />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.signUpRequests,
        element: (
          <RoleBasedGuard
            RoleAccess={[Roles.SITE_ADMIN, Roles.SITE_ADMIN_R, Roles.CLIENT_ADMIN]}
          >
            <RequestPage />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.demoVideos,
        element: (
          <RoleBasedGuard>
            <VideoWalkThrough />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.dataLayer,
        element: (
          <RoleBasedGuard
            RoleAccess={[Roles.CLIENT_ADMIN]}
          >
            <DataLayer />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.dataLayerRequest,
        element: (
          <RoleBasedGuard
            RoleAccess={[Roles.SITE_ADMIN, Roles.SITE_ADMIN_R]}
          >
            <DataLayerRequest />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.analytics,
        element: (
          <RoleBasedGuard>
            <Superset />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.networks,
        element: (
          <RoleBasedGuard
            RoleAccess={[Roles.SITE_ADMIN, Roles.SITE_ADMIN_R, Roles.CLIENT_ADMIN]}
          >
            <Networks />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.networkCreation,
        element: (
          <RoleBasedGuard
            RoleAccess={[Roles.SITE_ADMIN]}
            clientAccess
          >
            <NetworkCreation />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.performanceReport,
        element: (
          <RoleBasedGuard productAccessRoutes={[LocusProducts.Perform]}>
            <PerformanceReport />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.reportCreation,
        element: (
          <RoleBasedGuard productAccessRoutes={[LocusProducts.Perform]}>
            <PerformanceReportCreation />
          </RoleBasedGuard>
        ),
        children: [
          {
            path: Routes.reportStepDataset,
            element: (
              <ReportCreationContext
                Component={ReportDatasetSelect}
              />
            )
          },
          {
            path: Routes.reportStepReport,
            element: (
              <ReportCreationContext
                Component={ReportTypeSelect}
              />
            )
          },
          {
            path: Routes.reportStepDates,
            element: (
              <ReportCreationContext
                Component={ReportDateRanges}
              />
            )
          },
          {
            path: Routes.reportStepDateAdvanced,
            element: (
              <ReportCreationContext
                Component={ReportAdvancedDate}
              />
            )
          },
          {
            path: `${Routes.reportStepDateAdvanced}/:id`,
            element: (
              <ReportCreationContext
                Component={ReportAdvancedDate}
              />
            )
          },
          {
            path: Routes.reportStepTimes,
            element: (
              <ReportCreationContext
                Component={ReportTimePeriods}
              />
            )
          },
          {
            path: Routes.reportStepTimeAdvanced,
            element: (
              <ReportAdvancedTime />
            )
          },
          {
            path: `${Routes.reportStepTimeAdvanced}/:id`,
            element: (
              <ReportAdvancedTime />
            )
          },
          {
            path: Routes.reportStepRoutes,
            element: (
              <ReportCreationContext
                Component={ReportRoutesMap}
              />
            )
          },
          {
            path: Routes.reportStepRouteAdvanced,
            element: (
              <ReportCreationContext
                Component={ReportAdvancedRoute}
              />
            )
          },
          {
            path: `${Routes.reportStepRouteAdvanced}/:id`,
            element: (
              <ReportCreationContext
                Component={ReportAdvancedRoute}
              />
            )
          },
          {
            path: Routes.reportStepArea,
            element: (
              <ReportCreationContext
                Component={ReportAreaMap}
              />
            )
          },
          {
            path: Routes.reportStepAreaAdvanced,
            element: (
              <ReportCreationContext
                Component={ReportAdvancedArea}
              />
            )
          },
          {
            path: `${Routes.reportStepAreaAdvanced}/:id`,
            element: (
              <ReportCreationContext
                Component={ReportAdvancedArea}
              />
            )
          }
        ]
      },
      {
        path: `${Routes.performanceReport}/:id/view`,
        element: (
          <RoleBasedGuard productAccessRoutes={[LocusProducts.Perform]}>
            <ReportDetails />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.selectLink,
        element: (
          <RoleBasedGuard
            productAccessRoutes={[LocusProducts.CustomODAnalytics]}
          >
            <SelectLinkReport />
          </RoleBasedGuard>
        )
      },
      {
        path: Routes.selectLinkCreation,
        element: (
          <RoleBasedGuard
            clientAccess
            productAccessRoutes={[LocusProducts.CustomODAnalytics]}
          >
            <SelectLinkCreation />
          </RoleBasedGuard>
        )
      },
      {
        path: `${Routes.selectLink}/:id/view`,
        element: (
          <RoleBasedGuard productAccessRoutes={[LocusProducts.CustomODAnalytics]}>
            <ReportDetails />
          </RoleBasedGuard>
        )
      },
    ]
  },
  {
    path: '*',
    element: (
      <ErrorGuard />
    )
  }
];

export default routes;
